import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider } from '@/contexts/AppContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AdminAuthProvider } from '@/contexts/AdminAuthContext';
import { AuthProvider } from '@/contexts/AuthContext';
import OAuthCallback from '@/components/auth/OAuthCallback';
import ErrorBoundary from '@/components/ErrorBoundary';
import { SkipLink } from '@/components/SkipLink';
import { Toaster } from '@/components/ui/toaster';
import { BrandedLoadingScreen } from '@/components/common/BrandedLoading';
import HomePage from '@/pages/HomePage';

const CoachPage = lazy(() => import('@/pages/CoachPage'));
import OnboardingPage from '@/pages/OnboardingPage';
import AuthPage from '@/pages/AuthPage';
import VerifyEmailPage from '@/pages/VerifyEmailPage';
import TermsPage from '@/pages/TermsPage';
import { FeaturesPage } from '@/pages/FeaturesPage';
import { AboutPage } from '@/pages/AboutPage';
import { BlogPage } from '@/pages/BlogPage';
import { BlogPostPage } from '@/pages/BlogPostPage';
import { ContactPage } from '@/pages/ContactPage';
import { CareersPage } from '@/pages/CareersPage';
import { ChangelogPage } from '@/pages/ChangelogPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { SecurityPage } from '@/pages/SecurityPage';
import { GdprPage } from '@/pages/GdprPage';
import { PricingPage } from '@/pages/PricingPage';
import { TestimonialsPage } from '@/pages/TestimonialsPage';
import AdminLoginPage from '@/pages/admin/AdminLoginPage';
import { AdminLayout } from '@/components/admin/AdminLayout';
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import ContentListPage from '@/pages/admin/contents/ContentListPage';
import ContentDetailPage from '@/pages/admin/contents/ContentDetailPage';
import ContentFormPage from '@/pages/admin/contents/ContentFormPage';
import SurveyListPage from '@/pages/admin/surveys/SurveyListPage';
import SurveyDetailPage from '@/pages/admin/surveys/SurveyDetailPage';
import SurveyFormPage from '@/pages/admin/surveys/SurveyFormPage';
import SkillListPage from '@/pages/admin/skills/SkillListPage';
import SkillDetailPage from '@/pages/admin/skills/SkillDetailPage';
import SkillFormPage from '@/pages/admin/skills/SkillFormPage';
import OccupationListPage from '@/pages/admin/occupations/OccupationListPage';
import OccupationDetailPage from '@/pages/admin/occupations/OccupationDetailPage';
import OccupationFormPage from '@/pages/admin/occupations/OccupationFormPage';
import WinListPage from '@/pages/admin/wins/WinListPage';
import WinDetailPage from '@/pages/admin/wins/WinDetailPage';
import WinFormPage from '@/pages/admin/wins/WinFormPage';
import PainPointListPage from '@/pages/admin/pain-points/PainPointListPage';
import PainPointDetailPage from '@/pages/admin/pain-points/PainPointDetailPage';
import PainPointFormPage from '@/pages/admin/pain-points/PainPointFormPage';
import NudgeListPage from '@/pages/admin/nudges/NudgeListPage';
import NudgeDetailPage from '@/pages/admin/nudges/NudgeDetailPage';
import NudgeFormPage from '@/pages/admin/nudges/NudgeFormPage';
import LearningPlanListPage from '@/pages/admin/learning-plans/LearningPlanListPage';
import LearningPlanDetailPage from '@/pages/admin/learning-plans/LearningPlanDetailPage';
import LearningPlanFormPage from '@/pages/admin/learning-plans/LearningPlanFormPage';
import TipListPage from '@/pages/admin/tips/TipListPage';
import TipDetailPage from '@/pages/admin/tips/TipDetailPage';
import TipFormPage from '@/pages/admin/tips/TipFormPage';
import TidbitListPage from '@/pages/admin/tidbits/TidbitListPage';
import TidbitDetailPage from '@/pages/admin/tidbits/TidbitDetailPage';
import TidbitFormPage from '@/pages/admin/tidbits/TidbitFormPage';
import HackListPage from '@/pages/admin/hacks/HackListPage';
import HackDetailPage from '@/pages/admin/hacks/HackDetailPage';
import HackFormPage from '@/pages/admin/hacks/HackFormPage';
import LessonListPage from '@/pages/admin/lessons/LessonListPage';
import LessonDetailPage from '@/pages/admin/lessons/LessonDetailPage';
import LessonFormPage from '@/pages/admin/lessons/LessonFormPage';
import NanoListPage from '@/pages/admin/nanos/NanoListPage';
import NanoDetailPage from '@/pages/admin/nanos/NanoDetailPage';
import NanoFormPage from '@/pages/admin/nanos/NanoFormPage';
import AssessmentListPage from '@/pages/admin/assessments/AssessmentListPage';
import AssessmentDetailPage from '@/pages/admin/assessments/AssessmentDetailPage';
import AssessmentFormPage from '@/pages/admin/assessments/AssessmentFormPage';
import ReflectionListPage from '@/pages/admin/reflections/ReflectionListPage';
import ReflectionDetailPage from '@/pages/admin/reflections/ReflectionDetailPage';
import ReflectionFormPage from '@/pages/admin/reflections/ReflectionFormPage';
import NotificationTemplateListPage from '@/pages/admin/notifications/templates/NotificationTemplateListPage';
import NotificationTemplateDetailPage from '@/pages/admin/notifications/templates/NotificationTemplateDetailPage';
import NotificationTemplateFormPage from '@/pages/admin/notifications/templates/NotificationTemplateFormPage';
import NotificationTemplateStatsPage from '@/pages/admin/notifications/templates/NotificationTemplateStatsPage';
import SubscriptionPlanListPage from '@/pages/admin/subscription-plans/SubscriptionPlanListPage';
import SubscriptionPlanDetailPage from '@/pages/admin/subscription-plans/SubscriptionPlanDetailPage';
import SubscriptionPlanFormPage from '@/pages/admin/subscription-plans/SubscriptionPlanFormPage';
import SubscriptionPlanImportPage from '@/pages/admin/subscription-plans/SubscriptionPlanImportPage';
import SubscriptionPlanStatsPage from '@/pages/admin/subscription-plans/SubscriptionPlanStatsPage';
import PaymentGatewayListPage from '@/pages/admin/payment-gateways/PaymentGatewayListPage';
import PaymentGatewayDetailPage from '@/pages/admin/payment-gateways/PaymentGatewayDetailPage';
import PaymentGatewayFormPage from '@/pages/admin/payment-gateways/PaymentGatewayFormPage';
import AffiliatorGroupListPage from '@/pages/admin/affiliators/AffiliatorGroupListPage';
import AffiliatorGroupDetailPage from '@/pages/admin/affiliators/AffiliatorGroupDetailPage';
import AffiliatorGroupFormPage from '@/pages/admin/affiliators/AffiliatorGroupFormPage';
import AffiliatorGroupStatsPage from '@/pages/admin/affiliators/AffiliatorGroupStatsPage';
import PermissionListPage from '@/pages/admin/permissions/PermissionListPage';
import PermissionDetailPage from '@/pages/admin/permissions/PermissionDetailPage';
import PermissionFormPage from '@/pages/admin/permissions/PermissionFormPage';
import ApiKeysListPage from '@/pages/admin/api-keys/ApiKeysListPage';
import PermissionStatsPage from '@/pages/admin/permissions/PermissionStatsPage';
import RoleListPage from '@/pages/admin/roles/RoleListPage';
import RoleDetailPage from '@/pages/admin/roles/RoleDetailPage';
import RoleFormPage from '@/pages/admin/roles/RoleFormPage';
import RoleStatsPage from '@/pages/admin/roles/RoleStatsPage';
import ProfileListPage from '@/pages/admin/profiles/ProfileListPage';
import ProfileDetailPage from '@/pages/admin/profiles/ProfileDetailPage';
import ProfileFormPage from '@/pages/admin/profiles/ProfileFormPage';
import ProfileBioEditPage from '@/pages/admin/profiles/ProfileBioEditPage';
import PromptListPage from '@/pages/admin/prompts/PromptListPage';
import PromptDetailPage from '@/pages/admin/prompts/PromptDetailPage';
import PromptWriteTestPage from '@/pages/admin/prompts/PromptWriteTestPage';
import RevenueDashboardPage from '@/pages/admin/analytics/RevenueDashboardPage';
import SubscriptionAnalyticsPage from '@/pages/admin/analytics/SubscriptionAnalyticsPage';
import UserReportPage from '@/pages/admin/analytics/UserReportPage';
import LLMCostReportPage from '@/pages/admin/analytics/LLMCostReportPage';
import UserLearningPathPage from '@/pages/UserLearningPathPage';
import { ActivePlanDashboardPage } from '@/pages/ActivePlanDashboardPage';
import { LearningPlanPage } from '@/pages/LearningPlanPage';
import { UserProfilePage } from '@/pages/UserProfilePage';
import { NotificationPreferencesPage } from '@/pages/settings/NotificationPreferencesPage';
import OnboardingCompletionPage from '@/pages/OnboardingCompletionPage';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import CookieConsentBanner from '@/components/common/CookieConsentBanner';
import { trackPageView } from '@/services/activityTracker';
import DataPrivacyPage from '@/pages/settings/DataPrivacyPage';
import PaymentCheckoutPage from '@/pages/billing/PaymentCheckoutPage';
import { StripeProvider } from '@/components/billing/StripeProvider';
import BillingDashboardPage from '@/pages/billing/BillingDashboardPage';
import BillingHistoryPage from '@/pages/billing/BillingHistoryPage';
import InvoiceDetailPage from '@/pages/billing/InvoiceDetailPage';
import PlanChangePage from '@/pages/billing/PlanChangePage';
import CancelSubscriptionPage from '@/pages/billing/CancelSubscriptionPage';
import UsageDashboardPage from '@/pages/billing/UsageDashboardPage';

/** Tracks page views on every route change. Fire-and-forget. */
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AppProvider>
          <AdminAuthProvider>
          <AuthProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <PageViewTracker />
              <SkipLink />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/onboarding" element={<OnboardingPage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/login" element={<AuthPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/auth/callback/:provider" element={<OAuthCallback />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/changelog" element={<ChangelogPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/security" element={<SecurityPage />} />
                <Route path="/gdpr" element={<GdprPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/testimonials" element={<TestimonialsPage />} />
                {/* Protected routes — wait for auth bootstrap before rendering */}
                <Route path="/coach" element={<ProtectedRoute><Suspense fallback={<BrandedLoadingScreen message="Loading Coach…" />}><CoachPage /></Suspense></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
                <Route path="/settings/notifications" element={<ProtectedRoute><NotificationPreferencesPage /></ProtectedRoute>} />
                <Route path="/settings/privacy" element={<ProtectedRoute><DataPrivacyPage /></ProtectedRoute>} />
                <Route path="/checkout" element={<ProtectedRoute><StripeProvider><PaymentCheckoutPage /></StripeProvider></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute><BillingDashboardPage /></ProtectedRoute>} />
                <Route path="/billing/history" element={<ProtectedRoute><BillingHistoryPage /></ProtectedRoute>} />
                {/* Backwards-compatible aliases (UI links use these paths) */}
                <Route path="/billing/plans" element={<ProtectedRoute><PlanChangePage /></ProtectedRoute>} />
                <Route path="/billing/invoices" element={<ProtectedRoute><BillingHistoryPage /></ProtectedRoute>} />
                <Route path="/billing/invoices/:invoiceId" element={<ProtectedRoute><InvoiceDetailPage /></ProtectedRoute>} />
                <Route path="/billing/invoice/:invoiceId" element={<ProtectedRoute><InvoiceDetailPage /></ProtectedRoute>} />
                <Route path="/billing/change-plan" element={<ProtectedRoute><PlanChangePage /></ProtectedRoute>} />
                <Route path="/billing/cancel" element={<ProtectedRoute><CancelSubscriptionPage /></ProtectedRoute>} />
                <Route path="/billing/usage" element={<ProtectedRoute><UsageDashboardPage /></ProtectedRoute>} />
                <Route path="/profile/:userEmail" element={<ProtectedRoute><OnboardingCompletionPage /></ProtectedRoute>} />
                <Route path="/learning-path/:userEmail" element={<ProtectedRoute><UserLearningPathPage /></ProtectedRoute>} />
                <Route path="/learning-plan" element={<ProtectedRoute><LearningPlanPage /></ProtectedRoute>} />
                <Route path="/learning-plan/:userEmail" element={<ProtectedRoute><LearningPlanPage /></ProtectedRoute>} />
                {/* Dashboard: redirect to user profile (bio, learning status, activity) */}
                <Route path="/dashboard" element={<Navigate to="/profile" replace />} />
                <Route path="/my-learning/:userEmail" element={<ProtectedRoute><ActivePlanDashboardPage /></ProtectedRoute>} />
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route path="dashboard" element={<AdminDashboardPage />} />
                  <Route path="contents" element={<ContentListPage />} />
                  <Route path="contents/new" element={<ContentFormPage />} />
                  <Route path="contents/:id" element={<ContentDetailPage />} />
                  <Route path="contents/:id/edit" element={<ContentFormPage />} />
                  <Route path="prompts" element={<PromptListPage />} />
                  <Route path="prompts/write-test" element={<PromptWriteTestPage />} />
                  <Route path="prompts/:id" element={<PromptDetailPage />} />
                  <Route path="surveys" element={<SurveyListPage />} />
                  <Route path="surveys/new" element={<SurveyFormPage />} />
                  <Route path="surveys/:id" element={<SurveyDetailPage />} />
                  <Route path="surveys/:id/edit" element={<SurveyFormPage />} />
                  <Route path="skills" element={<SkillListPage />} />
                  <Route path="skills/new" element={<SkillFormPage />} />
                  <Route path="skills/:id" element={<SkillDetailPage />} />
                  <Route path="skills/:id/edit" element={<SkillFormPage />} />
                  <Route path="occupations" element={<OccupationListPage />} />
                  <Route path="occupations/new" element={<OccupationFormPage />} />
                  <Route path="occupations/:id" element={<OccupationDetailPage />} />
                  <Route path="occupations/:id/edit" element={<OccupationFormPage />} />
                  <Route path="wins" element={<WinListPage />} />
                  <Route path="wins/new" element={<WinFormPage />} />
                  <Route path="wins/:id" element={<WinDetailPage />} />
                  <Route path="wins/:id/edit" element={<WinFormPage />} />
                  <Route path="pain-points" element={<PainPointListPage />} />
                  <Route path="pain-points/new" element={<PainPointFormPage />} />
                  <Route path="pain-points/:id" element={<PainPointDetailPage />} />
                  <Route path="pain-points/:id/edit" element={<PainPointFormPage />} />
                  {/* Learning Management Routes */}
                  {/* Learning Plans */}
                  <Route path="learning-plans" element={<LearningPlanListPage />} />
                  <Route path="learning-plans/new" element={<LearningPlanFormPage />} />
                  <Route path="learning-plans/:id" element={<LearningPlanDetailPage />} />
                  <Route path="learning-plans/:id/edit" element={<LearningPlanFormPage />} />
                  
                  {/* Nudges */}
                  <Route path="nudges" element={<NudgeListPage />} />
                  <Route path="nudges/new" element={<NudgeFormPage />} />
                  <Route path="nudges/:id" element={<NudgeDetailPage />} />
                  <Route path="nudges/:id/edit" element={<NudgeFormPage />} />
                  
                  {/* Tips */}
                  <Route path="tips" element={<TipListPage />} />
                  <Route path="tips/new" element={<TipFormPage />} />
                  <Route path="tips/:id" element={<TipDetailPage />} />
                  <Route path="tips/:id/edit" element={<TipFormPage />} />
                  
                  {/* Tidbits */}
                  <Route path="tidbits" element={<TidbitListPage />} />
                  <Route path="tidbits/new" element={<TidbitFormPage />} />
                  <Route path="tidbits/:id" element={<TidbitDetailPage />} />
                  <Route path="tidbits/:id/edit" element={<TidbitFormPage />} />
                  
                  {/* Hacks */}
                  <Route path="hacks" element={<HackListPage />} />
                  <Route path="hacks/new" element={<HackFormPage />} />
                  <Route path="hacks/:id" element={<HackDetailPage />} />
                  <Route path="hacks/:id/edit" element={<HackFormPage />} />
                  
                  {/* Lessons */}
                  <Route path="lessons" element={<LessonListPage />} />
                  <Route path="lessons/new" element={<LessonFormPage />} />
                  <Route path="lessons/:id" element={<LessonDetailPage />} />
                  <Route path="lessons/:id/edit" element={<LessonFormPage />} />
                  
                  {/* Nanos */}
                  <Route path="nanos" element={<NanoListPage />} />
                  <Route path="nanos/new" element={<NanoFormPage />} />
                  <Route path="nanos/:id" element={<NanoDetailPage />} />
                  <Route path="nanos/:id/edit" element={<NanoFormPage />} />
                  
                  {/* Assessments */}
                  <Route path="assessments" element={<AssessmentListPage />} />
                  <Route path="assessments/new" element={<AssessmentFormPage />} />
                  <Route path="assessments/:id" element={<AssessmentDetailPage />} />
                  <Route path="assessments/:id/edit" element={<AssessmentFormPage />} />
                  
                  {/* Reflections */}
                  <Route path="reflections" element={<ReflectionListPage />} />
                  <Route path="reflections/new" element={<ReflectionFormPage />} />
                  <Route path="reflections/:id" element={<ReflectionDetailPage />} />
                  <Route path="reflections/:id/edit" element={<ReflectionFormPage />} />
                  
                  {/* Notification Templates */}
                  <Route path="notifications/templates" element={<NotificationTemplateListPage />} />
                  <Route path="notifications/templates/new" element={<NotificationTemplateFormPage />} />
                  <Route path="notifications/templates/stats" element={<NotificationTemplateStatsPage />} />
                  <Route path="notifications/templates/:templateId" element={<NotificationTemplateDetailPage />} />
                  <Route path="notifications/templates/:templateId/edit" element={<NotificationTemplateFormPage />} />
                  
                  {/* Subscription Plans */}
                  <Route path="subscription-plans" element={<SubscriptionPlanListPage />} />
                  <Route path="subscription-plans/new" element={<SubscriptionPlanFormPage />} />
                  <Route path="subscription-plans/import" element={<SubscriptionPlanImportPage />} />
                  <Route path="subscription-plans/stats" element={<SubscriptionPlanStatsPage />} />
                  <Route path="subscription-plans/:id" element={<SubscriptionPlanDetailPage />} />
                  <Route path="subscription-plans/:id/edit" element={<SubscriptionPlanFormPage />} />
                  
                  {/* Payment Gateways */}
                  <Route path="payment-gateways" element={<PaymentGatewayListPage />} />
                  <Route path="payment-gateways/new" element={<PaymentGatewayFormPage />} />
                  <Route path="payment-gateways/:id/edit" element={<PaymentGatewayFormPage />} />
                  <Route path="payment-gateways/:id" element={<PaymentGatewayDetailPage />} />
                  
                  {/* Affiliators */}
                  <Route path="affiliators" element={<AffiliatorGroupListPage />} />
                  <Route path="affiliators/new" element={<AffiliatorGroupFormPage />} />
                  <Route path="affiliators/stats" element={<AffiliatorGroupStatsPage />} />
                  <Route path="affiliators/:id" element={<AffiliatorGroupDetailPage />} />
                  <Route path="affiliators/:id/edit" element={<AffiliatorGroupFormPage />} />
                  
                  {/* Permissions */}
                  <Route path="permissions" element={<PermissionListPage />} />
                  <Route path="permissions/new" element={<PermissionFormPage />} />
                  <Route path="permissions/statistics" element={<PermissionStatsPage />} />
                  <Route path="permissions/:id" element={<PermissionDetailPage />} />
                  <Route path="permissions/:id/edit" element={<PermissionFormPage />} />
                  
                  {/* Roles */}
                  <Route path="roles" element={<RoleListPage />} />
                  <Route path="roles/new" element={<RoleFormPage />} />
                  <Route path="roles/statistics" element={<RoleStatsPage />} />
                  <Route path="roles/:id" element={<RoleDetailPage />} />
                  <Route path="roles/:id/edit" element={<RoleFormPage />} />
                  
                  {/* User Profiles */}
                  <Route path="profiles" element={<ProfileListPage />} />
                  <Route path="profiles/:userName" element={<ProfileDetailPage />} />
                  <Route path="profiles/:userName/edit" element={<ProfileFormPage />} />
                  <Route path="profiles/:userName/bio/edit" element={<ProfileBioEditPage />} />

                  {/* Analytics */}
                  <Route path="analytics/revenue" element={<RevenueDashboardPage />} />
                  <Route path="analytics/subscriptions" element={<SubscriptionAnalyticsPage />} />
                  <Route path="analytics/users" element={<UserReportPage />} />
                  <Route path="analytics/llm-costs" element={<LLMCostReportPage />} />

                  {/* API Keys (super-admin only) */}
                  <Route path="api-keys" element={<ApiKeysListPage />} />
                </Route>
              </Routes>
              <CookieConsentBanner />
            </Router>
            <Toaster />
          </AuthProvider>
          </AdminAuthProvider>
        </AppProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;

